import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import ReactDOM from 'react-dom';
import Moveable from 'react-moveable';
import Selecto from 'react-selecto';
import { Frame, setAlias } from 'scenejs';
import { Icon, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { getTranslateValues } from '../../../utils/transformHelpers';
import { updateNodeStylesForComplexText as updateNodeStyles, setNodeFrame, setTableDataStyles } from '../../../utils/nodeHelpers';
import { stylesToJson } from '../../../utils/JsonConversion/node2Json';
import { updateSvgValues, parseSvgData } from '../../../utils/svgUtils';
// pages context API
import { PageElementsContext } from '../../../context/pageElementsContext';
import { PageSettingsContext } from '../../../context/pageSettingsContext';
import { RefTag } from '../../../artboard/Page/styles';
import { ElementIndexHighlighter } from '../styles';
import { DOCUMENT_DESIGN_SECTION } from '../../../artboard/translationValues';
import {
  FILLABLE_TEXT_INPUT_TYPE,
  FILLABLE_SIGNATURE_TYPE,
  TEXT_ELEMENT,
  FILLABLE_COMBO_BOX_TYPE,
  FILLABLE_RADIO_BUTTON_GROUP_TYPE,
  ELEMENT_TYPES,
  STATIC_TABLE_ELEMENT,
  COMPLEX_DOCUMENT_PAGE_WRAPPER,
  FILLABLE_CHECKBOX_TYPE,
  FILLABLE_RADIO_BUTTON_TYPE,
  IMAGE_ELEMENT
} from '../../../utils/complexDocumentConstants';
import { ComplexEditingContext } from '../../../context/complexEditingContext';
import { ELEMENT_POSITION_TOOLBAR_ID, ElementPositionToolbar } from '../../../artboard/Toolbar/elementPositionToolbar';
import { getClipboardData } from '../../../utils/helper';
import AccessibilityIcon from '../../../components/Accessibility/AccessibilityIcon';
import { getGroupIdAndNonGroupElements } from '../../../utils/groupHelper';

const NAMESPACES = ['DOCUMENT_DESIGN_SECTION'];

const CustomAble = {
    name: "customAble",
    props: [
        "customAble",
        "prop1",
    ],
    events: [],
    render(moveable) {

        // Add key (required)
        // Add class prefix moveable-(required)
        const height = moveable.props?.target?.clientHeight;
        const elementIndex = moveable.props?.target?.dataset
          ? (parseInt(moveable.props?.target?.dataset['elementIndex'], 10) + 1) : null;
        // console.log("moveable.props.prop1 :: ", moveable.props, elementIndex);
        if (!elementIndex) {
          return <div/>;
        }
        return <div key="custom-element" className="moveable-custom" style={{ position: 'relative', height }}>
          <ElementIndexHighlighter>
            {elementIndex}
          </ElementIndexHighlighter>
        </div>;
    },
}

const Selectable = props => {
  setAlias('tx', ['transform', 'translateX']);
  setAlias('ty', ['transform', 'translateY']);
  setAlias('tr', ['transform', 'rotate']);
  setAlias('sx', ['transform', 'scaleX']);
  setAlias('sy', ['transform', 'scaleY']);
  setAlias('matrix3d', ['transform', 'matrix3d']);
  const [targets, setTargets] = useState([]);
  const moveableRef = React.useRef(null);
  const selectoRef = React.useRef(null);
  const changesRef = React.useRef(false);
  const frameMapRef = useRef(new Map());
  const [zoom, setZoom] = useState(null);
  const { onFontClick, pageId, droppedElements, getTargets, setSelectedGroupElementId, isHeaderElement = false } = props;
  const [isElementEditing, setElementEditing] = useState(false);
  const [elementGuidelines, setElementGuidelines] = useState([]);
  const [canSnap, setSnapping] = useState(true);
  const [snapThreshold, setSnapThreshold] = useState(5);
  const [refElNameVisible,setRefElNameVisible] = useState(true);
  const [targetPosition, setTargetPosition] = useState();
  const [showAccessibility, setShowAccessibility] = useState(false);
  const [accessibilityPopupVisible, setAccessibilityPopupVisible] = useState(false);
  const [showImageLink, setShowImageLink] = useState(false);
  const [imageLinkPopupVisible, setImageLinkPopupVisible] = useState(false);
  const [startingCoords, setStartingCoords] = useState(null);
  const { clearTargets, editable, onKeyPress = () => {}, isEmailTemplate = false, moveableReference = undefined, staticTableBlock = false, isInteractiveDesign } = props;
  const imageRef = useContext(ComplexEditingContext);
  const { t } = useTranslation(NAMESPACES);
  const frameMap = frameMapRef.current;

  const {
    storedElements,
    updateComplexElementDetails: updateStoredElementDetails,
    updateComplexElementDetailsBulk,
    updatedElementStyles,
    isTableEditing,
    generatingText,
    setMovableClick,
  } = useContext(PageElementsContext);
  const { pageSettings } = useContext(PageSettingsContext);

  useEffect(() => {
    const blockElement = document.getElementById(pageId);
    const selectGroupElements = event => {
      const groupId = event?.detail?.groupId;
      if (groupId && editable) {
        const groupElements = Array.from(document.querySelectorAll(`[data-group-uuid='${groupId}']`));
        updateGroupSelection(groupElements);
      }
    };
    if (blockElement) {
      blockElement.addEventListener('focusGroupElement', selectGroupElements);
    }
    return () => {
      if (blockElement) {
        blockElement.removeEventListener('focusGroupElement', selectGroupElements);
      }
    };
  }, [pageId, editable]);

  useEffect(() => {
    const blockElement = document.getElementById(pageId);
    const removeHighlight = e => {
      setAllTargets([]);
      props.setSelectedElementIds();
    }
    if (blockElement) {
      blockElement.addEventListener('removeHighlight', removeHighlight);
    }
    return () => {
      if (blockElement) {
        blockElement.removeEventListener('removeHighlight', removeHighlight);
      }
    }
  }, [pageId]);

  useEffect(() => {
    const blockElement = document.getElementById(pageId);
    const highlightElement = e => {
      const newTargets = document.querySelector(`div[data-uuid="${e?.detail?.elementUUID}"]`);
      setTimeout(() => {
        setAllTargets([newTargets]);
      }, 1000);
    }
    if (blockElement) {
      blockElement.addEventListener('highlightElement', highlightElement);
    }
    return () => {
      if (blockElement) {
        blockElement.removeEventListener('highlightElement', highlightElement);
      }
    }
  }, [pageId]);

  useEffect(() => {
    const clearSelection = zoom !== pageSettings.zoom;
    if (clearTargets || clearSelection || !editable) {
      setAllTargets([]);
      if (selectoRef.current) {
        selectoRef.current.setSelectedTargets([]);
      }
    }
    setZoom(pageSettings.zoom);
  }, [clearTargets, editable, pageSettings.zoom, zoom, selectoRef]);

  useEffect(() => {
    setElementEditing(false);
    if (props.clearTargets) {
      const newTargets = Array.from(document.querySelectorAll('.selectable')).filter(el => props.selectedElementIds.includes(el.getAttribute('data-uuid')));
      setAllTargets(newTargets);
      if (selectoRef.current) {
        selectoRef.current.setSelectedTargets(newTargets);
        setViewPort(newTargets);
      }
    }
  }, [props.selectedElementIds, props.clearTargets]);

  useEffect(() => {
    const updatedSelectableElements = [];
    const targetPage = document.getElementById(`${pageId}`);
    if (targetPage && targetPage.querySelectorAll('.selectable')) {
      let selectableElements = targetPage.querySelectorAll('.selectable');
      if (selectableElements && selectableElements.length > 0) {
        for (let index = 0; index < selectableElements.length; index++) {
          updatedSelectableElements.push(selectableElements[index]);
          // TODO : Check if element type is svg / rectangle / line / doubleline and save to imageRef the JSON of the SVG
          if (
            selectableElements[index].getAttribute('data-element-type') === 'svg' ||
            selectableElements[index].getAttribute('data-element-type') === 'line' ||
            selectableElements[index].getAttribute('data-element-type') === 'rectangle' ||
            selectableElements[index].getAttribute('data-element-type') === 'circle' ||
            selectableElements[index].getAttribute('data-element-type') === 'doubleLine'
          ) {
            setImage(selectableElements[index]);
          }
        }
      }
    }
    setElementGuidelines(updatedSelectableElements);
  }, [pageId, droppedElements, clearTargets]);

  /**
   * Create new frame and assign it to frameMap*/
  const newFrame = (el, styles) => {
    const rotate = (styles && styles.rotate) || 0;
    const { x, y } = getTranslateValues(el);
    let extraProperties = {};
    if (Array.from(el.classList).includes('group-selectable') || Array.from(el.classList).includes('group-selectable-el')) {
      extraProperties = {
        width: `${el.offsetWidth}px`,
        height: `${el.offsetHeight}px`,
      }
    }
    const frame = new Frame({
      transform: {
        translateX: x ? `${x}px` : '0px',
        translateY: y ? `${y}px` : '0px',
        // translateZ: '5px',
        rotate: `${rotate}deg`,
      },
      ...extraProperties
    });

    frameMap.set(el, frame);
    return frame;
  };

  const getFrame = (target, styles = {}) => {
    return frameMap.get(target) || newFrame(target, styles);
  };

  const setImage = async(target) => {
    // check if deep childNodes exists because interactive design as svg's converted to img while moving from draft to QA/Publish. 
    if (target && target.childNodes?.[0]?.childNodes?.[0]?.outerHTML) {
      const svgString = target.childNodes[0].childNodes[0].outerHTML;
      const svgJSON = await parseSvgData(svgString);
      imageRef.current.set(target.getAttribute('data-uuid'), { json: svgJSON, svg: svgString });
      return imageRef.current.get(target.getAttribute('data-uuid'));
    }
  };

  const setAllTargets = useCallback((newTargets) => {
    setTargets(() => {
      return newTargets;
    })
  }, []);

  useEffect(() => {
    const isDragging = moveableRef?.current?.isDragging();
    if (targets && targets.length > 0 && updatedElementStyles && !isDragging) {
      
      const applyStyleUpdate = (styleUpdate) => {
        if (!styleUpdate.uuid) return;
        
        const targetElement = targets.find(tar => tar.getAttribute('data-uuid') === styleUpdate.uuid);

        if (targetElement) {
          const frame = getFrame(targetElement);

          if (styleUpdate.translateX) {
            frame.set('tx', styleUpdate.translateX);
          }
          if (styleUpdate.x) {
            frame.set('tx', `${styleUpdate.x}px`);
          }
          if (styleUpdate.translateY) {
            frame.set('ty', styleUpdate.translateY);
          }
          if (styleUpdate.y) {
            frame.set('ty', `${styleUpdate.y}px`);
          }
          if (styleUpdate.rotate) {
            frame.set('tr', styleUpdate.rotate);
          }
          if (styleUpdate.width) {
            frame.set('width', styleUpdate.width);
          }
          if (styleUpdate.height) {
            frame.set('height', styleUpdate.height);
          }
          if (targetElement.getAttribute('data-element-type') === 'dynamic-table') {
            Array.from(targetElement.querySelectorAll('.column-selectable')).forEach(columnTarget => {
              const columnFrame = getFrame(columnTarget);
              columnFrame.set('width', `${columnTarget.clientWidth}px`);
              Array.from(columnTarget.children).forEach((subTarget, index) => {
                if (index < 3) {
                  const subFrame = getFrame(subTarget);
                  subFrame.set('width', `${subTarget.clientWidth}px`);
                }
              })
            })
          } else if (targetElement.getAttribute('data-element-type') === 'checkboxGroup' ||
          targetElement.getAttribute('data-element-type') === 'radiobuttongroup') {
            Array.from(targetElement.querySelectorAll('.block-selectable')).forEach(blockTarget => {
              const blockFrame = getFrame(blockTarget);
              const { x, y } = getTranslateValues(blockTarget);
              blockFrame.set('tx', `${x}px`);
              blockFrame.set('ty', `${y}px`);
              blockFrame.set('width', `${blockTarget.clientWidth}px`);
              blockFrame.set('height', `${blockTarget.clientHeight}px`);
            })
          }

          if (styleUpdate.override) {
            const type = targetElement.getAttribute('data-element-type');
            if (['svg', 'line', 'rectangle', 'circle', 'doubleLine'].includes(type)) {
              setImage(targetElement);
            }
          }
        }
      };

      if (Array.isArray(updatedElementStyles)) {
        updatedElementStyles.forEach(style => applyStyleUpdate(style));
      } else {
        applyStyleUpdate(updatedElementStyles);
      }

      setViewPort(targets, true);
      if (generatingText) {
        moveableRef.current && moveableRef.current.updateRect();
      } else {
        setTimeout(() =>{
          moveableRef.current && moveableRef.current.updateRect();
        }, 100);
      }
    }
    // eslint-disable-next-line
  }, [updatedElementStyles]);

  useEffect(() => {
    if (props.editable) {
      moveableReference.current = moveableRef.current;
    }
  }, [props.editable]);

  const handleKeyboard = useCallback(async e => {
    const targetParent = e.target.parentElement;
    // return if quill editor is the target element or target element's parent is quill tooltip input box i.e, link input
    if (e.target.classList.contains('ql-editor') || (targetParent && targetParent.classList.contains('ql-tooltip'))) return;
    // return if target is not inside 'complex-page-wrapper'
    const el = document.getElementById(COMPLEX_DOCUMENT_PAGE_WRAPPER);
    if (!el.contains(e.target)) return;

    const copiedElements= await getClipboardData();

    if (e && e.keyCode && moveableRef && moveableRef.current && ((targets && targets.length > 0) || (copiedElements?.pdsCopiedElements && copiedElements?.pdsCopiedElements.length))) {
      switch (e.keyCode) {
        case 37:
          // Arrow Left
          moveableRef.current.request('draggable', { deltaX: -4, isInstant: true });
          e.preventDefault();
          break;
        case 38:
          // Arrow Up
          moveableRef.current.request('draggable', { deltaY: -4, isInstant: true });
          e.preventDefault();
          break;
        case 39:
          // Arrow Right
          moveableRef.current.request('draggable', { deltaX: 4, isInstant: true });
          e.preventDefault();
          break;
        case 40:
          // Arrow Down
          moveableRef.current.request('draggable', { deltaY: 4, isInstant: true });
          e.preventDefault();
          break;
        default:
          if (onKeyPress) {
            onKeyPress(e);
          }
          break;
      }
    }
  }, [targets, moveableRef?.current, selectoRef?.current, onKeyPress]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyboard, true);
    return () => {
      document.removeEventListener('keydown', handleKeyboard, true);
    };
  }, [handleKeyboard]);

  const updateGroupBorder = (parentNode) => {
    const borderNode = parentNode.querySelector('#border-el');
    if (borderNode) {
        let elementLeastXPosition = 0, elementLeastYPosition = 0, elementRightBottomXPosition = 0, elementRightBottomYPosition = 0, blockTranslateX = 0, blockTranslateY = 0;
        const elementNodes = parentNode.querySelectorAll('.selectable');
        const elementPositions = Array.from(elementNodes).map(node => {
          const nodeFrame = getFrame(node);
          return ({
            x: parseInt(nodeFrame.get('transform', 'translateX').replace('px', '')),
            y: parseInt(nodeFrame.get('transform', 'translateY').replace('px', '')),
            width: parseInt(nodeFrame.get('width')?.replace('px', '')),
            height: parseInt(nodeFrame.get('height')?.replace('px', '')),
          })
        });
        elementPositions.forEach((positions, index) => {
           const { x, y, width, height } = positions;
           if (index === 0) {
             elementLeastXPosition = x;
             elementLeastYPosition = y;
             elementRightBottomXPosition = x + width;
             elementRightBottomYPosition = y + height;
           } else {
             if (x < elementLeastXPosition) {
               elementLeastXPosition = x;
             }
             if (x + width > elementRightBottomXPosition) {
               elementRightBottomXPosition = x + width;
             }
             if (y < elementLeastYPosition) {
               elementLeastYPosition = y;
             }
             if (y + height > elementRightBottomYPosition) {
               elementRightBottomYPosition = y + height;
             }
         }
        });
        blockTranslateX = elementLeastXPosition;
        blockTranslateY = elementLeastYPosition;
        let width = elementRightBottomXPosition - elementLeastXPosition; // TODO : Check if this has to be divided by scaling
        let height = elementRightBottomYPosition - elementLeastYPosition;
        const borderFrame = getFrame(borderNode);
        borderFrame.set('transform', 'translateX', `${blockTranslateX}px`);
        borderFrame.set('transform', 'translateY', `${blockTranslateY}px`);
        borderFrame.set('width', `${width}px`);
        borderFrame.set('height', `${height}px`);
        let styles = borderNode.style.cssText;
        styles = stylesToJson(styles);
        borderNode.style.cssText += getFrame(borderNode, styles).toCSS();
    }
  };

  const handleRender = ({ target }, isGroup = false, skipGroupBorder = false) => {
    target.style.cssText += getFrame(target).toCSS();
    if (Array.from(target.classList).includes('group-selectable-el') && !skipGroupBorder) {
      const parentNode = target.parentNode.parentNode;
      updateGroupBorder(parentNode);
    }
    if (target.getAttribute('data-element-type') === 'referenced') {
      const subTargets = target.querySelectorAll('.block-selectable');
      subTargets.forEach(subTarget => {
        /*let styles = subTarget.style.cssText;
        styles = stylesToJson(styles);
        subTarget.style.cssText += getFrame(subTarget, styles).toCSS();*/
        handleRender({ target: subTarget });
      })
    } else if (target.getAttribute('data-element-type') === 'dynamic-table') {
      const titleTarget = target.querySelectorAll(`#title-${target.getAttribute('data-uuid')}`)[0];
      if (titleTarget) {
        let styles = titleTarget.style.cssText;
        styles = stylesToJson(styles);
        titleTarget.style.cssText += getFrame(titleTarget, styles).toCSS();
      }
      Array.from(target.querySelectorAll('.column-selectable')).forEach((columnTarget, columnIndex) => {
        let styles = columnTarget.style.cssText;
        styles = stylesToJson(styles);
        columnTarget.style.cssText += getFrame(columnTarget, styles).toCSS();
        Array.from(columnTarget.children).forEach((subTarget, index) => {
          if (index < 3) {
            let styles = subTarget.style.cssText;
            styles = stylesToJson(styles);
            subTarget.style.cssText += getFrame(subTarget, styles).toCSS();
            if (index === 1) {
              const heightTarget = subTarget.children[0];
              let styles = heightTarget.style.cssText;
              styles = stylesToJson(styles);
              heightTarget.style.cssText += getFrame(heightTarget, styles).toCSS();
            }
            if (index === 2) {
              Array.from(subTarget.children).forEach(row => {
                let styles = row.style.cssText;
                styles = stylesToJson(styles);
                row.style.cssText += getFrame(row, styles).toCSS();
              })
            }
          }
        })
      })
    } else if (target.getAttribute('data-element-type') === 'checkboxGroup' ||
    target.getAttribute('data-element-type') === 'radiobuttongroup') {
      Array.from(target.querySelectorAll('.block-selectable')).forEach((blockTarget, columnIndex) => {
        let styles = blockTarget.style.cssText;
        styles = stylesToJson(styles);
        blockTarget.style.cssText += getFrame(blockTarget, styles).toCSS();
      });
    }
  };

  const handleRenderGroup = ({ targets }) => {
    const parentNodes = new Set();
    targets.forEach(target => {
      handleRender({ target }, false, true);
      if (Array.from(target.classList).includes('group-selectable-el')) {
        parentNodes.add(target.parentNode.parentNode);
      }
    });
    parentNodes.forEach(parentNode => updateGroupBorder(parentNode));
  };

  const handleDragStart = ({ target, set, inputEvent, inputTarget, ...rest }) => {
    changesRef.current = false;
    if ((target.getAttribute('data-element-type') === STATIC_TABLE_ELEMENT
      && (typeof inputEvent?.target?.className === 'string' && inputEvent?.target?.className.includes("resizer")))
    ) return false;
    if ((target.getAttribute('data-element-type') === 'checkboxGroup' ||
    target.getAttribute('data-element-type') === 'radiobuttongroup') && inputEvent?.target) {
      let parentSelectable = inputEvent?.target;
      do {
        parentSelectable = parentSelectable.parentNode;
      } while(parentSelectable &&
        parentSelectable.id !== 'root' &&
        !Array.from(parentSelectable?.classList || []).includes('selectable') &&
        !Array.from(parentSelectable?.classList || []).includes('add-button'));
      if (parentSelectable && Array.from(parentSelectable?.classList || []).includes('group-selectable-el')) {
          // setTimeout(() => {
            selectoRef.current.clickTarget(inputEvent, inputTarget);
          // }, 1);
          return false;
      } else if (parentSelectable && Array.from(parentSelectable?.classList || []).includes('add-button')) {
        return false;
      }
    }
    const frame = getFrame(target);
    set([
      parseFloat(frame.get('transform', 'translateX')),
      parseFloat(frame.get('transform', 'translateY')),
    ]);
    if(startingCoords && (target.clientX !== startingCoords.x || target.clientY !== startingCoords.y)) {
      setMovableClick(true);
    }
    setTimeout(() => {
      onFontClick(false);
    }, 0);
  };

  const handleDrag = ({ target, beforeTranslate, clientX, clientY }) => {
    changesRef.current = true;
    setRefElNameVisible(false)
    const frame = getFrame(target);
    frame.translate = beforeTranslate;
    frame.set('transform', 'translateX', `${beforeTranslate[0]}px`);
    frame.set('transform', 'translateY', `${beforeTranslate[1]}px`);
    if (targets && targets.length === 1) {
      // below state is set so we can get object left and top when dragged and passed to element position toolbar on top.
      setTargetPosition({left: beforeTranslate[0], top: beforeTranslate[1]});
    }
  };

  const handleDragGroupStart = ({ events }) => {
    events.forEach(handleDragStart);
  };

  const handleDragGroup = ({ events }) => {
    events.forEach(handleDrag);
  };

  const handleRotateStart = ({ target, set }) => {
    changesRef.current = false;
    const frame = getFrame(target);
    set(parseFloat(frame.get('tr')));
  };

  const handleRotate = ({ target, beforeDelta, clientX, clientY }) => {
    changesRef.current = true;
    setRefElNameVisible(false)
    const frame = getFrame(target);
    const deg = parseFloat(frame.get('tr')) + beforeDelta;
    frame.set('tr', `${deg}deg`);
    // target.style.cssText += frame.toCSS();
    moveableRef.current && moveableRef.current.updateRect();
    if (targets && targets.length === 1) {
      setLabel(clientX, clientY, `R: ${deg.toFixed(1)}`);
    }
  };

  const handleRotateGroupStart = ({ targets, events }) => {
    // events.forEach(this.handleRotateStart);
    events.forEach(({ target, set, dragStart }) => {
      const frame = getFrame(target);
      const tx = parseFloat(frame.get('transform', 'translateX')) || 0;
      const ty = parseFloat(frame.get('transform', 'translateY')) || 0;
      const rotate = parseFloat(frame.get('tr')) || 0;
      set(rotate);
      if (dragStart) {
        dragStart.set([tx, ty]);
      }
    });
  };

  const handleRotateGroup = ({ events }) => {
    events.forEach(({ target, beforeRotate, drag }) => {
      const frame = getFrame(target);
      const beforeTranslate = drag.beforeTranslate;
      frame.set('tr', `${beforeRotate}deg`);
      frame.set('transform', 'translateX', `${beforeTranslate[0]}px`);
      frame.set('transform', 'translateY', `${beforeTranslate[1]}px`);
      // target.style.cssText += frame.toCSS();
    });
  };

  const handleResizeStart = ({ target, setOrigin, dragStart }) => {
    changesRef.current = false;
    setNodeFrame(target);
    if (target.querySelector(`[id^='q-container-']`) !== null) {
      Array.from(target.querySelectorAll(`[id^='q-container-']`)).forEach((editor) => {
        editor.dispatchEvent(
          new CustomEvent('resizeStart')
        );
      });
    }
    if (target.getAttribute('data-element-type') === STATIC_TABLE_ELEMENT) {
      setTableDataStyles([...Array.from(target.querySelectorAll('td')), ...Array.from(target.querySelectorAll('col'))]);
    }

    setOrigin(['%', '%']);
    const frame = getFrame(target);
    if (dragStart) {
      dragStart.set([parseFloat(frame.get('tx')), parseFloat(frame.get('ty'))]);
    }
    if (parseFloat(frame.get('tr')) !== 0) {
      setSnapping(false);
    }
    setSnapThreshold(0);
    setViewPort([target], true);
  };
  
  const positionAccessibilityIcon = () => {
    const icon = document.getElementsByClassName('accessibility-icon')[0];
    if (icon) {
      const elementWidth = moveableRef?.current?.refTargets[0].offsetWidth;
      icon.style.left = `${(elementWidth/2) - (icon.offsetWidth/2)}px`;
    }
  }

  const handleResize = ({ target, width, height, drag, direction, clientX, clientY, delta, isGroupElement = false, activeUuids = [] }) => {
    changesRef.current = true;
    const frame = getFrame(target);
    if (direction[0] !== 0) {
      frame.set('tx', `${drag.beforeTranslate[0]}px`);
    }
    let newWidth = width;
    let newHeight = height;
    const selectedTargetType = target.getAttribute('data-element-type');
    positionAccessibilityIcon();
    if (direction && direction[1] !== 0 && direction[0] !== 0) { // Scaling operation - affects both width and height
      let scaling;
      if (
        (direction[0] === 1 && direction[1] === 1) ||
        (direction[0] === 1 && direction[1] === -1)
      ) {
        scaling = height / Number(target.getAttribute('data-viewheight'));
      } else {        
        scaling = width / Number(target.getAttribute('data-viewport'));
      }
      newWidth = scaling * Number(target.getAttribute('data-viewport'));
      newHeight = scaling * Number(target.getAttribute('data-viewheight'));
      frame.set('width', `${newWidth.toFixed(3)}px`);
      frame.set('height', `${newHeight.toFixed(3)}px`);
      frame.set('ty', `${drag.beforeTranslate[1]}px`);      
      if(target.getAttribute('data-element-type') === FILLABLE_COMBO_BOX_TYPE) {
        // updateStoredElementDetails(target.getAttribute('data-uuid'), { size: (newHeight/2).toFixed(2) }, 'style');
        target.style.fontSize = `${(newHeight/2).toFixed(2)}px`;
      }
      if (target.getAttribute('data-element-type') === 'text') {
        console.log("scaling :: ", scaling, newWidth, newHeight, delta);
        target.setAttribute('data-scaling', scaling);
        updateNodeStyles(
          target.querySelectorAll('.ql-replica'),
          Number.parseFloat(scaling).toFixed(3),
          false
        );
      } else if (target.getAttribute('data-element-type') === 'referenced') {
        const subTargets = target.querySelectorAll('.block-selectable');
        subTargets.forEach((subTarget) => {
          if (subTarget && subTarget.getAttribute('data-viewport')) {
            /*subFrame.set('tx', `${scaling * subTarget.getAttribute('data-translateX')}px`);*/
            const beforeTranslate = [scaling * subTarget.getAttribute('data-translateX'), scaling * subTarget.getAttribute('data-translateY')];
            const newWidth = scaling * Number(subTarget.getAttribute('data-viewport'));
            const newHeight = scaling * Number(subTarget.getAttribute('data-viewheight'));
            handleResize({ target: subTarget, width: newWidth, height: newHeight, drag: { beforeTranslate }, direction, clientX, clientY, delta });
          }
        });
      } else if (target.getAttribute('data-element-type') === 'dynamic-table') {
        const titleTarget = target.querySelectorAll(`#title-${target.getAttribute('data-uuid')}`)[0];
        let titleFrame;
        if (titleTarget) {
          titleFrame = getFrame(titleTarget);
          titleFrame.set('height', `${titleTarget.querySelectorAll('.ql-editor')[0].clientHeight}px`);
          updateStoredElementDetails(titleTarget.getAttribute('data-uuid'), { height: `${titleTarget.querySelectorAll('.ql-editor')[0].clientHeight}px` }, 'style');
        }
        Array.from(target.querySelectorAll('.column-selectable')).forEach((columnTarget, columnIndex) => {
          const columnFrame = getFrame(columnTarget);
          const newWidth = scaling * Number(columnTarget.getAttribute('data-viewport'));
          columnFrame.set('width', `${newWidth.toFixed(3)}px`);
          Array.from(columnTarget.children).forEach((subTarget, index) => {
            if (index < 3) {
              const subFrame = getFrame(subTarget);
              const newWidth = scaling * Number(subTarget.getAttribute('data-viewport'));
              subFrame.set('width', `${newWidth.toFixed(3)}px`);
              if (titleFrame) {
                subFrame.set('height', titleFrame.get('height'));
              }
              if (index === 1) {
                const childFrame = getFrame(subTarget.children[0]);
                const newHeight = scaling * Number(subTarget.children[0].getAttribute('data-viewheight'));
                childFrame.set('height', `${newHeight.toFixed(3)}px`);
              }
              if (index === 2) {
                Array.from(subTarget.children).forEach(row => {
                  const childFrame = getFrame(row);
                  const newHeight = scaling * Number(row.getAttribute('data-viewheight'));
                  childFrame.set('height', `${newHeight.toFixed(3)}px`);
                })
              }
            }
          })
        })
      } else if (target.getAttribute('data-element-type') === 'checkboxGroup' ||
      target.getAttribute('data-element-type') === 'radiobuttongroup') {
        Array.from(target.querySelectorAll('.block-selectable')).forEach((blockTarget, columnIndex) => {
          const blockFrame = getFrame(blockTarget);
          const newWidth = scaling * Number(blockTarget.getAttribute('data-viewport'));
          const newHeight = scaling * Number(blockTarget.getAttribute('data-viewheight'));
          blockFrame.set('tx', `${scaling * Number(blockTarget.getAttribute('data-translateX'))}px`);
          blockFrame.set('ty', `${scaling * Number(blockTarget.getAttribute('data-translateY'))}px`);
          blockFrame.set('width', `${newWidth}px`);
          blockFrame.set('height', `${newHeight}px`);
        });
      } else if (target.getAttribute('data-element-type') === FILLABLE_COMBO_BOX_TYPE) {
        const newHeight = scaling * Number(target.getAttribute('data-viewheight'));
        target.style.fontSize = newHeight / 1.5;
      } else if (target.getAttribute('data-element-type') === STATIC_TABLE_ELEMENT) {
        target.setAttribute('data-scaling', scaling);
        Array.from(target.querySelectorAll('col')).map(cell => {
          const originalWidth = parseInt(cell.getAttribute('data-viewport'));
          cell.style.width = `${originalWidth * scaling}px`;
        });
        Array.from(target.querySelectorAll('td')).map(cell => {
          const originalHeight = parseInt(cell.getAttribute('data-viewheight'));
          cell.style.height = `${originalHeight * scaling}px`;

          const paddingTop = parseInt(cell.getAttribute('data-paddingtop'));
          cell.style.paddingTop = `${paddingTop * scaling}px`;
          const paddingBottom = parseInt(cell.getAttribute('data-paddingbottom'));
          cell.style.paddingBottom = `${paddingBottom * scaling}px`;
          const paddingLeft = parseInt(cell.getAttribute('data-paddingleft'));
          cell.style.paddingLeft = `${paddingLeft * scaling}px`;
          const paddingRight = parseInt(cell.getAttribute('data-paddingright'));
          cell.style.paddingRight = `${paddingRight * scaling}px`;
        });
        updateNodeStyles(
          target.querySelectorAll('.ql-replica'),
          Number.parseFloat(scaling).toFixed(3),
          false
        );
      } else if (selectedTargetType === FILLABLE_CHECKBOX_TYPE || selectedTargetType === FILLABLE_RADIO_BUTTON_TYPE) {
        if (!isGroupElement) {
          const currentUuid = target.getAttribute('data-uuid');
          const el = storedElements.find(el => el.uuid === currentUuid);
          if (el) {
            const filteredElements = storedElements.filter(
              els =>
                el.uuid !== els.uuid &&
                selectedTargetType === els?.fillableType &&
                els?.formOptions?.groupName === el?.formOptions?.groupName,
            );
            filteredElements.forEach(subElement => {
              if (activeUuids.includes(subElement.uuid)) return;
              const subTarget = document.querySelector(`[data-uuid='${subElement.uuid}']`);
              const beforeTranslate = [subElement.style.x, subElement.style.y];
              const x = subElement.style.x;
              const y = subElement.style.y;
              subTarget.setAttribute('data-viewheight', newHeight);
              subTarget.setAttribute('data-viewport', newWidth);
              handleResize({
                target: subTarget,
                width: newWidth,
                height: newHeight,
                drag: { beforeTranslate },
                direction,
                clientX: x,
                clientY: y,
                delta,
                isGroupElement: true,
              });
            });
          }
        }
      } else if (target.getAttribute('data-element-type') === 'line' || target.getAttribute('data-element-type') === 'doubleLine') {
        const currentSvgJson = imageRef.current.get(target.getAttribute('data-uuid'))?.json;
        const { stringSVG, parsedString } = updateSvgValues({
            width: newWidth,
            height: newHeight,
            scaling: true,
            scale: scaling,
            svgElement: true
          },
          {
            elementDetails: {
              style: {},
              value: currentSvgJson,
              uiType: target.getAttribute('data-element-type'),
            }
          },
          'style'
        );
        target.childNodes[0].innerHTML = stringSVG;
        imageRef.current.set(target.getAttribute('data-uuid'), { json: parsedString, svg: stringSVG });
      }
      if (targets && targets.length === 1) {
        const calculatedWidth = (newWidth.toFixed(3) * (25.4 / 96)).toFixed(1);
        const calculatedHeight = (newHeight.toFixed(3) * (25.4 / 96)).toFixed(1);
        setLabel(
          clientX,
          clientY,
          `width: ${calculatedWidth}mm<br/>height: ${calculatedHeight}mm`,
        );
      }
    } else {
      if (
        target &&
        target.getAttribute('data-element-type') &&
        (target.getAttribute('data-element-type') === 'line' ||
          target.getAttribute('data-element-type') === 'doubleLine')
      ) {
        // Update element frame width and height
        frame.set('width', `${width.toFixed(3)}px`);
        const frameHeight = target.getAttribute('data-viewheight');
        if (frameHeight && Number(frameHeight) > 0) {
          frame.set('height', `${Number.parseFloat(frameHeight).toFixed(3)}px`);
          let svgElement = target.childNodes[0]?.childNodes[0];

          if (target && target.getAttribute('data-uuid') && svgElement) {
              // Get stored element details
              const updatedSVGDetails = {
                width: width.toFixed(3),
                height: Number.parseFloat(frameHeight).toFixed(3),
                // TODO : Figure out how to set transform value in element without using updateSVGValues method
                // transform: `translateX(${drag.beforeTranslate[0]}px) translateY(${drag.beforeTranslate[1]}px) rotate(${frame.properties.transform.rotate}) scaleX(1) scaleY(1)`,
              };
              const { parsedString, stringSVG } = updateSvgValues(
                {...updatedSVGDetails, svgElement: true, isUrl: false },
                {
                  elementDetails: {
                    style: {},
                    value: imageRef.current.get(target.getAttribute('data-uuid'))?.json,
                    uiType: target.getAttribute('data-element-type')
                  }
                },
                'style'
              );
              target.childNodes[0].innerHTML = stringSVG;
              imageRef.current.set(target.getAttribute('data-uuid'), { json: parsedString, svg: stringSVG });
          }
        }

        if (direction[0] === 0) {
          frame.set('ty', `${drag.beforeTranslate[1]}px`);
        }
        if (targets && targets.length === 1) {
          const calculatedWidth = (width.toFixed(3) * (25.4 / 96)).toFixed(1);
          setLabel(clientX, clientY, `width: ${calculatedWidth}mm<br/>`);
        }
      } else if (target.getAttribute('data-element-type') === 'text') {
        // TODO : Should update width of element
        if (direction[1] === -1) {
          frame.set('ty', `${drag.beforeTranslate[1]}px`);
        }
        frame.set('width', `${width}px`);
        const editorOffsetContainer = target.querySelectorAll('.ql-editor');
        let editorOffsetHeight = height;
        if (
          editorOffsetContainer &&
          editorOffsetContainer.length > 0 &&
          editorOffsetContainer[1]
        ) {
          editorOffsetHeight = editorOffsetContainer[1].offsetHeight;
          frame.set('height', `${editorOffsetHeight}px`);
        }
        if (targets && targets.length === 1) {
          const calculatedWidth = (width * (25.4 / 96)).toFixed(1);
          const calculatedHeight = (editorOffsetHeight * (25.4 / 96)).toFixed(1);
          setLabel(clientX, clientY, `width: ${calculatedWidth}mm<br/>height: ${calculatedHeight}mm`);
        }
      } else if (target.getAttribute('data-element-type') === STATIC_TABLE_ELEMENT) {
        if (direction[1] === -1) {
          frame.set('ty', `${drag.beforeTranslate[1]}px`);
        }
        frame.set('width', `${width}px`);
        let newHeight;
        const tableContainer = target.querySelector('table');
        const originalTableWidth = parseInt(target.getAttribute('data-viewport'));
        const widthScaling = (width / originalTableWidth);
          Array.from(target.querySelectorAll('col')).map(cell => {
            const originalWidth = parseInt(cell.getAttribute('data-viewport'));
            cell.style.width = `${originalWidth * widthScaling}px`;
          });
          newHeight = tableContainer.offsetHeight;
          frame.set('height', `${newHeight}px`);
        if (targets && targets.length === 1) {
          const calculatedWidth = (width * (25.4 / 96)).toFixed(1);
          setLabel(clientX, clientY, `width: ${calculatedWidth}mm<br/>height: ${newHeight}mm`);
        }
      } else if (target &&
        target.getAttribute('data-element-type') &&
        target.getAttribute('data-element-type') === 'dynamic-table' &&
        direction && direction[0] === 0 && direction[1] !== 0
      ) {
        if (direction[1] === -1) {
          frame.set('ty', `${drag.beforeTranslate[1]}px`);
        }
        frame.set('height', `${height}px`);
      } else if (target &&
        target.getAttribute('data-element-type') &&
        (target.getAttribute('data-element-type') === 'checkboxGroup' ||
               target.getAttribute('data-element-type') === 'radiobuttongroup')
      ) {
        if (direction[1] === -1) {
          frame.set('ty', `${drag.beforeTranslate[1]}px`);
        } else if (direction[0] === -1) {
          frame.set('tx', `${drag.beforeTranslate[0]}px`);
        }
        const targetWidth = Number(target.getAttribute('data-viewport'));
        const textTargets = Array.from(target.querySelectorAll('.block-selectable')).filter(subTarget => subTarget.getAttribute('data-element-type') === 'text');
        if (direction[1] !== 0) {
          frame.set('height', `${height}px`);
        } else if (direction[0] !== 0) {
          frame.set('width', `${width}px`);
          // TODO : Min width to be calculated from text width and restricted
          if (target.getAttribute('data-checkbox-direction') === 'vertical') {
            textTargets.forEach(subTarget => {
              const subFrame = getFrame(subTarget);
              const subTargetWidth = Number(subTarget.getAttribute('data-viewport'));
              subFrame.set('width', `${subTargetWidth + (width - targetWidth)}px`);
            });
          }
        }
      } else if (target &&
        target.getAttribute('data-element-type') &&
        target.getAttribute('data-element-type') === FILLABLE_TEXT_INPUT_TYPE ||
        target.getAttribute('data-element-type') === FILLABLE_COMBO_BOX_TYPE ||
        target.getAttribute('data-element-type') === FILLABLE_SIGNATURE_TYPE
      ) {
        if(direction && direction[0] === 0 && direction[1] !== 0){
          if (direction[1] === -1) {
            frame.set('ty', `${drag.beforeTranslate[1]}px`);
          }
          frame.set('height', `${height}px`);
        }
        if(direction && direction[0] !== 0 && direction[1] === 0){
          if (direction[1] === -1) {
            frame.set('tx', `${drag.beforeTranslate[1]}px`);
          }
          frame.set('width', `${width}px`);
        }
      }
      else if (target &&
        target.getAttribute('data-element-type') &&
        target.getAttribute('data-element-type') === 'rectangle') {
      } else {
        const editorOffsetContainer = target.querySelectorAll('.ql-editor');
        let editorOffsetHeight = height;
        if (
          editorOffsetContainer &&
          editorOffsetContainer.length > 0 &&
          editorOffsetContainer[0].offsetHeight &&
          direction[0] !== 0
        ) {
          if (
            target &&
            target.getAttribute('data-type') &&
            target.getAttribute('data-type') === 'table'
          ) {
            const tableContainer = target.querySelectorAll('.editor-table');
            editorOffsetHeight = tableContainer[0].offsetHeight + 2;
          } else {
            editorOffsetHeight = editorOffsetContainer[0].offsetHeight;
          }
        }
        frame.set('width', `${width}px`);
        frame.set('height', `${editorOffsetHeight}px`);
        if (target) {
          target.setAttribute('data-viewport', Number(width));
          target.setAttribute('data-viewheight', Number(editorOffsetHeight));
        }
        if (direction[0] === 0) {
          frame.set('ty', `${drag.beforeTranslate[1]}px`);
        }
        if (targets && targets.length === 1) {
          const calculatedWidth = (width * (25.4 / 96)).toFixed(1);
          const calculatedHeight = (editorOffsetHeight * (25.4 / 96)).toFixed(1);
          setLabel(clientX, clientY, `width: ${calculatedWidth}mm<br/>height: ${calculatedHeight}mm`);
        }
      }
    }
    if (target &&
      target.getAttribute('data-element-type') &&
      target.getAttribute('data-element-type') === 'svg') {
      let svgElement = target.childNodes[0]?.childNodes[0];
      if (svgElement) {
        const newElement = {
          elementDetails: {
            value: imageRef.current.get(target.getAttribute('data-uuid'))?.json,
            style: {}
          }
        };
        const { parsedString, stringSVG } = updateSvgValues({
            width,
            height,
            svgElement: true
          },
          newElement,
          'style'
        );
        imageRef.current.set(target.getAttribute('data-uuid'), { json: parsedString, svg: stringSVG });
        target.childNodes[0].innerHTML = stringSVG;
      }
    } else if (target &&
      target.getAttribute('data-element-type') &&
      (target.getAttribute('data-element-type') === 'rectangle' || target.getAttribute('data-element-type') === 'circle')) {
      let svgElement = target.childNodes[0]?.childNodes[0];

      const currentSvgJson = imageRef.current.get(target.getAttribute('data-uuid'))?.json;
      let scaling;
      const oldWidth = Number(target.getAttribute('data-viewport'));
      const oldHeight = Number(target.getAttribute('data-viewheight'));
      if (direction[1] !== 0 && direction[0] !== 0) {
        if (
          (direction[0] === 1 && direction[1] === 1) ||
          (direction[0] === 1 && direction[1] === -1)
        ) {
          scaling = height / oldHeight;
        } else {
          scaling = width / oldWidth;
        }
      } else {
        if (direction[0] === 0) {
          scaling = height / oldHeight;
        } else if (direction[1] === 0) {
          scaling = width / oldWidth;
        }
      }

      console.log({ oldWidth, oldHeight, scaling });
      if (svgElement) {
        if (direction[0] === 0) {
          newHeight = height;
          newWidth = oldWidth;
          frame.set('height', `${height}px`);
          frame.set('ty', `${drag.beforeTranslate[1]}px`);
        }
        if (direction[1] === 0) {
          newWidth = width;
          newHeight = oldHeight;
          frame.set('width', `${width}px`);
          frame.set('tx', `${drag.beforeTranslate[0]}px`);
        }
        /*if (target) {
          target.setAttribute('data-viewport', Number(newWidth));
          target.setAttribute('data-viewheight', Number(newHeight));
        }*/
        const calculatedWidth = (Number(newWidth).toFixed(3) * (25.4 / 96)).toFixed(1);
        const calculatedHeight = (Number(newHeight).toFixed(3) * (25.4 / 96)).toFixed(1);
        setLabel(
          clientX,
          clientY,
          `width: ${calculatedWidth}mm<br/>height: ${calculatedHeight}mm`,
        );
        const { stringSVG, parsedString } = updateSvgValues({
            width: newWidth,
            height: newHeight,
            scaling: direction[1] !== 0 && direction[0] !== 0,
            scale: scaling,
            borderWidth: Number(target.getAttribute('data-border-width')),
            borderRadius: Number(target.getAttribute('data-border-radius')),
            svgElement: true
          },
          {
            elementDetails: {
              style: {},
              value: currentSvgJson,
              uiType: target.getAttribute('data-element-type'),
            }
          },
          'style'
        );
        target.childNodes[0].innerHTML = stringSVG;
        imageRef.current.set(target.getAttribute('data-uuid'), { json: parsedString, svg: stringSVG });
      }
    } 
  };

  const handleResizeGroupStart = ({ events }) => {
    events.forEach(handleResizeStart);
  };

  const handleResizeGroup = ({ events }) => {
    const activeUuids = events.map(ev => ev.target.getAttribute('data-uuid'));
    events.forEach(ev => handleResize({ ...ev, activeUuids }));
  };

  const handleScale = ({ target, delta, clientX, clientY, isPinch }) => {
    const frame = getFrame(target);
    const scaleX = frame.get('transform', 'scaleX') * delta[0];
    const scaleY = frame.get('transform', 'scaleY') * delta[1];
    frame.set('transform', 'scaleX', scaleX);
    frame.set('transform', 'scaleY', scaleY);
    // target.style.cssText += frame.toCSS();
  };

  const handleScaleGroup = ({ events }) => {
    events.forEach(ev => {
      const frame = getFrame(ev.target);
      frame.set('tx', `${parseFloat(frame.get('tx')) + ev.drag.beforeDelta[0]}px`);
      frame.set('ty', `${parseFloat(frame.get('ty')) + ev.drag.beforeDelta[1]}px`);
      frame.set('sx', frame.get('sx') * ev.delta[0]);
      frame.set('sy', frame.get('sy') * ev.delta[1]);
      // ev.target.style.cssText += frame.toCSS();
    });
  };

  const onEnd = () => {
    let target = document.querySelectorAll('.moveable-label')[0];
    target.style.display = 'none';
    // remove drag cursor from moveable target
    if (
      moveableRef &&
      moveableRef.current &&
      moveableRef.current.refTargets &&
      moveableRef.current.refTargets.length > 0
    ) {
      const moveableTarget = moveableRef.current.refTargets[0];
      moveableTarget.classList.remove('is-dragging');
    }
    setMovableClick(false);
  };

  const setViewPort = (selected, override = false) => {
    for (let i = 0; i < selected.length; i++) {
      const target = selected[i];
      const { offsetWidth, offsetHeight } = target;
      // Check if viewport of element is calculated
      if (override || !target.getAttribute('data-viewport')) {
        const { x, y } = getTranslateValues(target);
        if (target.style && target.style.width) {
          const targetWidth = target.style.width;
          if (targetWidth === '100%') {
            target.setAttribute('data-viewport', offsetWidth);
            target.setAttribute('data-viewheight', offsetHeight);
          } else {
            target.setAttribute('data-viewport', Number(targetWidth.replace(/px$/, '')));
            target.setAttribute('data-viewheight', offsetHeight);
          }
        } else if (offsetWidth && offsetHeight) {
          target.setAttribute('data-viewport', offsetWidth);
          target.setAttribute('data-viewheight', offsetHeight);
        }
        if (override || Array.from(target.classList).includes('group-selectable') || Array.from(target.classList).includes('group-selectable-el')) {
          target.setAttribute('data-translateX', x);
          target.setAttribute('data-translateY', y);
        }
        if (target &&
              target.getAttribute('data-element-type') &&
              target.getAttribute('data-element-type') === 'rectangle') {
          const childNode = target.children[0].children[0];
          target.setAttribute('data-border-width', childNode.getAttribute('data-border-width'));
          target.setAttribute('data-border-radius', childNode.getAttribute('data-border-radius'));
        }
      }
      if (target.getAttribute('data-element-type') === 'referenced') {
        setViewPort(target.querySelectorAll('.block-selectable'), true);
      }
      if (target.getAttribute('data-element-type') === 'dynamic-table') {
        setViewPort(target.querySelectorAll('.column-selectable'), true);
        setViewPort(target.querySelectorAll(`#title-${target.getAttribute('data-uuid')}`), true);
        Array.from(target.querySelectorAll('.column-selectable')).forEach((columnTarget) => {
          Array.from(columnTarget.children).forEach((subTarget, index) => {
            if (index < 3) {
              setViewPort([subTarget], true)
              if (index === 1) {
                setViewPort([subTarget.children[0]], true);
              }
              if (index === 2) {
                setViewPort(subTarget.children, true);
              }
            };
          })
        })
      } else if (target.getAttribute('data-element-type') === STATIC_TABLE_ELEMENT) {
        setTableDataStyles([...Array.from(target.querySelectorAll('td')), ...Array.from(target.querySelectorAll('col'))]);
      }
      if (Array.from(target.classList).includes('group-selectable')) {
        setViewPort(target.querySelectorAll('.block-selectable'), true);
      }
    }
  };

  const setLabel = (clientX, clientY, text) => {
    let target = document.querySelectorAll('.moveable-label')[0];
    target.style.cssText = `
display: block; transform: translate(${clientX}px, ${clientY -
      10}px) translate(-100%, -100%) translateZ(-100px);`;
    target.innerHTML = text;
    // add drag cursor to moveable target
    if (
      moveableRef &&
      moveableRef.current &&
      moveableRef.current.refTargets &&
      moveableRef.current.refTargets.length > 0
    ) {
      const moveableTarget = moveableRef.current.refTargets[0];
      moveableTarget.classList.add('is-dragging');
    }
  };

  const handleStoredElementUpdates = (target, direction, eventType, accumulatedUpdates = undefined) => {
    let styles = target.style.cssText;
    styles = stylesToJson(styles);
    
    const frame = getFrame(target, styles);
    console.log("handleStoredElementUpdates :: ", target, direction);

    const applyUpdate = (uuid, details, type) => {
      if (accumulatedUpdates) {
        accumulatedUpdates.push({ uuid, details, updationType: type });
      } else {
        updateStoredElementDetails(uuid, details, type);
      }
    };

    if (
      target &&
      target.getAttribute('data-uuid') &&
      frame &&
      frame.properties &&
      frame.properties.transform
    ) {
      const newStyles = {
        x: parseInt(`${frame.properties.transform.translateX}`.replaceAll('px', '')),
        y: parseInt(`${frame.properties.transform.translateY}`.replaceAll('px', '')),
        rotate: parseInt(`${frame.properties.transform.rotate}`.replaceAll('px', ''))
      };
      if (frame.properties.width) {
        newStyles.width = frame.properties.width;
      }
      if (frame.properties.height) {
        newStyles.height = frame.properties.height;
      }

      const frameHeight = target.getAttribute('data-viewheight');
      const frameWidth = target.getAttribute('data-viewport');

      if (
        target.getAttribute('data-element-type') &&
        (target.getAttribute('data-element-type') === 'svg' ||
          target.getAttribute('data-element-type') === 'line' ||
          target.getAttribute('data-element-type') === 'doubleLine' ||
          target.getAttribute('data-element-type') === 'rectangle' ||
          target.getAttribute('data-element-type') === 'circle')
      ) {
        if (frameHeight && Number(frameHeight) > 0 && frameWidth && Number(frameWidth) > 0) {
          if (target && target.getAttribute('data-uuid')) {
            const updatedSVGDetails = {
              width: Number.parseFloat(frameWidth).toFixed(3),
              height: Number.parseFloat(frameHeight).toFixed(3),
              scaling: direction && direction[1] !== 0 && direction[0] !== 0,
              x: newStyles.x,
              y: newStyles.y,
              rotate: newStyles.rotate
            };
            const { stringSVG } = updateSvgValues(
              updatedSVGDetails,
              {
                elementDetails: {
                  style: {},
                  value: imageRef.current.get(target.getAttribute('data-uuid'))?.json
                }
              },
              'style'
            );
            const updatedElementDetails = {
              url: stringSVG,
              style: newStyles,
            };
            applyUpdate(
              target.getAttribute('data-uuid'),
              updatedElementDetails,
              'svg-value-with-styles',
            );
          }
        }
      } else if (target.getAttribute('data-element-type') === 'referenced') {
        setTimeout(() => {
          const subTargets = target.querySelectorAll('.block-selectable');
          Array.from(subTargets).forEach(target => {
            handleStoredElementUpdates(target, direction, undefined, accumulatedUpdates);
          })
          setViewPort(subTargets, true);
          applyUpdate(target.getAttribute('data-uuid'), newStyles, 'style');
        }, 0);
      } else if (target.getAttribute('data-element-type') === 'dynamic-table') {
        let columnStyles = [];
        let rowStyles = [];
        let titleStyles = undefined;
        const titleTarget = target.querySelectorAll(`#title-${target.getAttribute('data-uuid')}`)[0];
        if (titleTarget) {
            titleStyles = {
              value: titleTarget.querySelectorAll('.ql-editor')[0].innerHTML,
              height: `${titleTarget.querySelectorAll('.ql-editor')[0].clientHeight}px`
            }
        }
        target.querySelectorAll('.column-selectable').forEach((columnTarget, columnIndex) => {
          if (columnIndex === 0) {
            const numberOfStyles = parseInt(columnTarget.children[2].getAttribute('data-row-styles'));
            rowStyles = Array.from(columnTarget.children[2].children).filter((a, index) => index < numberOfStyles).map((rowTarget, index) => {
              let rowStyle = JSON.parse(rowTarget.getAttribute('data-row-style'));
              rowStyle.size = parseInt(`${getComputedStyle(rowTarget.children[0].children[0].children[0]).fontSize}`.replace('px', ''));
              const borderWidth = parseFloat(getComputedStyle(rowTarget).borderLeftWidth.replace('px', ''));
              const clientHeight = parseFloat(getComputedStyle(rowTarget).height.replace('px', ''));
              rowStyle.height = clientHeight - (borderWidth);
              return rowStyle;
            });
          }
          columnStyles.push({
            width: `${columnTarget.clientWidth}px`,
            height: `${columnTarget.querySelectorAll('.react-resizable')[0].clientHeight}px`,
            value: columnTarget.querySelectorAll('.ql-editor')[0].innerHTML
          });
        })
        applyUpdate(target.getAttribute('data-uuid'), {
          elementStyles: newStyles,
          columnStyles,
          rowStyles,
          titleStyles
        }, 'table-moveable-styles');
        setViewPort([target], true);
      } else if (target.getAttribute('data-element-type') === 'text' ||  target.getAttribute('data-element-type') === FILLABLE_TEXT_INPUT_TYPE) {
        const refElement = target.querySelector(`#q-container-${target.getAttribute('data-uuid')}`);
        if (direction && direction[1] !== 0 && direction[0] !== 0) {
          const scaling = parseFloat(target.getAttribute('data-scaling', scaling));
          applyUpdate(
            target.getAttribute('data-uuid'),
            { scaling: parseFloat(scaling), refElement, style: newStyles },
            'text-scaling',
          );
        } else {
          refElement.dispatchEvent(new CustomEvent('resizeEnd', { detail: {} }));
          applyUpdate(target.getAttribute('data-uuid'), newStyles, 'style');
        }
      } else if (target.getAttribute('data-element-type') === STATIC_TABLE_ELEMENT) {
        if (newStyles.width) newStyles.width = parseInt(`${newStyles.width}`.replaceAll('px', ''));
        if (newStyles.height) newStyles.height = parseInt(`${newStyles.height}`.replaceAll('px', ''));
        if (direction && direction[1] !== 0 && direction[0] !== 0) {
          const scaling = parseFloat(target.getAttribute('data-scaling', scaling));
          applyUpdate(
            target.getAttribute('data-uuid'),
            { scaling: Math.round(scaling * 1000) / 1000, refElement: target.querySelector('table'), style: newStyles },
            'table-scaling',
          );
        } else if (direction && direction[0] !== 0 && direction[1] === 0) {
          applyUpdate(
            target.getAttribute('data-uuid'),
            { style: newStyles, refElement: target.querySelector('table') },
            'table-width-scaling',
          );
        } else {
          console.log({ newStyles: JSON.parse(JSON.stringify(newStyles)), frame });
          applyUpdate(target.getAttribute('data-uuid'), newStyles, 'style');
        }
      } else if (Array.from(target.classList).includes('group-selectable-el') || Array.from(target.classList).includes('group-selectable')) {
        let elementLeastXPosition = 0, elementLeastYPosition = 0, elementRightBottomXPosition = 0, elementRightBottomYPosition = 0, blockTranslateX = 0, blockTranslateY = 0;
        let parentNode = target;
        let parentFrame = getFrame(parentNode);
        let elementStyles = undefined;
        let isParentStyleAltered = false;
        if (Array.from(target.classList).includes('group-selectable-el')) {
          parentNode = target.parentNode.parentNode;
          parentFrame = getFrame(parentNode);
        } else {
          isParentStyleAltered = target.getAttribute('data-is-variable') === "1";
        }
        const currentTranslateX = parseInt(parentFrame.get('transform', 'translateX').replace('px', ''));
        const currentTranslateY = parseInt(parentFrame.get('transform', 'translateY').replace('px', ''));
        const elementNodes = parentNode.querySelectorAll('.block-selectable');
        const elementPositions = Array.from(elementNodes).map(node => {
          const nodeFrame = getFrame(node);
          return ({
            x: parseInt(nodeFrame.get('transform', 'translateX').replace('px', '')) + currentTranslateX,
            y: parseInt(nodeFrame.get('transform', 'translateY').replace('px', '')) + currentTranslateY,
            width: parseInt(nodeFrame.get('width')?.replace('px', '')),
            height: parseInt(nodeFrame.get('height')?.replace('px', '')),
          })
        });
        elementPositions.forEach((positions, index) => {
           const { x, y, width, height } = positions;
           if (index === 0) {
             elementLeastXPosition = x;
             elementLeastYPosition = y;
             elementRightBottomXPosition = x + width;
             elementRightBottomYPosition = y + height;
           } else {
             if (x < elementLeastXPosition) {
               elementLeastXPosition = x;
             }
             if (x + width > elementRightBottomXPosition) {
               elementRightBottomXPosition = x + width;
             }
             if (y < elementLeastYPosition) {
               elementLeastYPosition = y;
             }
             if (y + height > elementRightBottomYPosition) {
               elementRightBottomYPosition = y + height;
             }
         }
        });
        blockTranslateX = elementLeastXPosition;
        blockTranslateY = elementLeastYPosition;
        let width = elementRightBottomXPosition - elementLeastXPosition; // TODO : Check if this has to be divided by scaling
        let height = (elementRightBottomYPosition - elementLeastYPosition);
        if (isParentStyleAltered) {
          elementStyles = {
             x: blockTranslateX,
             y: blockTranslateY,
             rotate: 0,
             width: parentFrame.get('width'),
             height: parentFrame.get('height')
          };
        } else {
            elementStyles = {
               x: blockTranslateX,
               y: blockTranslateY,
               rotate: 0,
               width: `${width}px`,
               height: `${height}px`
            };
            parentFrame.set('transform', 'translateX', `${blockTranslateX}px`);
            parentFrame.set('transform', 'translateY', `${blockTranslateY}px`);
            parentFrame.set('width', `${width}px`);
            parentFrame.set('height', `${height}px`);
        }
        const borderNode = parentNode.querySelector('#border-el');
        if (borderNode) {
            borderNode.style.transform = `translateX(0px) translateY(0px) rotate(0) scaleX(1) scaleY(1)`;
            borderNode.style.width = width;
            borderNode.style.height = height;
        }
        const subGroupStyles = Array.from(parentNode.querySelectorAll('.block-selectable')).map((subGroupEl, subIndex) => {
          const subGroupFrame = getFrame(subGroupEl);
          const elementTranslateX = elementPositions[subIndex].x - blockTranslateX;
          const elementTranslateY = elementPositions[subIndex].y - blockTranslateY;
          subGroupFrame.set('transform', 'translateX', `${elementTranslateX}px`);
          subGroupFrame.set('transform', 'translateY', `${elementTranslateY}px`);
          subGroupFrame.set('width', `${elementPositions[subIndex].width}px`);
          subGroupFrame.set('height', `${elementPositions[subIndex].height}px`);
          subGroupEl.style.cssText += subGroupFrame.toCSS();
          let value = undefined;
          if (subGroupEl.getAttribute('data-element-type') === 'text') {
            value = subGroupEl.querySelector('.ql-editor').innerHTML
          }
          return {
            styles: {
              x: elementTranslateX,
              y: elementTranslateY,
              rotate: 0,
              width: `${elementPositions[subIndex].width}px`,
              height: `${elementPositions[subIndex].height}px`,
            },
            value
          }
        });
        console.log({ elementStyles, subGroupStyles });
        applyUpdate(parentNode.getAttribute('data-uuid'), { elementStyles, subGroupStyles }, 'group-styles');
        setViewPort([parentNode], true);
      } else if (target.getAttribute('data-element-type') === FILLABLE_RADIO_BUTTON_GROUP_TYPE) {
        if (target.getAttribute('data-element-index')) {
          const details = { elementIndex: parseInt(target.getAttribute('data-element-index')) };
          details.style = newStyles;
          applyUpdate(target.getAttribute('data-uuid'), details, 'group-style');
        }
      } else if (target.getAttribute('data-element-type') === ELEMENT_TYPES.CHART) {
        applyUpdate(target.getAttribute('data-uuid'), newStyles, 'style');
      } else {
        console.log("Coming here!! : ", newStyles, target.getAttribute('data-uuid'));
        applyUpdate(target.getAttribute('data-uuid'), newStyles, 'style');
      }
      setViewPort([target], true);
    }
  };

  const handleElementUpdates = (props, eventType) => {

    let { target, lastEvent } = props;

    setTargetPosition(undefined);
    setSnapThreshold(5);
    console.log('handleElementUpdates props',props, changesRef.current);
    // TODO : Check with thaya why this is not persisted
    if (
      lastEvent === undefined &&
      (target.getAttribute('data-element-type') === FILLABLE_COMBO_BOX_TYPE ||
        target.getAttribute('data-element-type') === FILLABLE_SIGNATURE_TYPE)
    ) {
      return;
    }
    setRefElNameVisible(true)
    if (changesRef.current === true) {
      handleStoredElementUpdates(target, lastEvent?.direction, eventType);
      if (eventType === 'ON_RESIZE_END') {
        handleCheckboxResize(target, lastEvent);
      }
      changesRef.current = false;
    } else if (target.getAttribute('data-element-type') === 'text' ||  target.getAttribute('data-element-type') === FILLABLE_TEXT_INPUT_TYPE) {
      const refElement = target.querySelector(`#q-container-${target.getAttribute('data-uuid')}`);
      refElement.dispatchEvent(new CustomEvent('resizeEnd', { detail: {} }));
    }
    onEnd();
    if (!canSnap) {
      setSnapping(true);
    }    
  };

  const onGroupEnd = ({ targets, lastEvent }) => {
    if (changesRef.current === true) {
      const updates = [];
      const activeUuids = Array.from(targets).map(t => t.getAttribute('data-uuid'));
      Array.from(targets).forEach((target, index) => {
        handleStoredElementUpdates(target, lastEvent?.direction, undefined, updates);
        handleCheckboxResize(target, lastEvent, updates, activeUuids);
      });
      if (updates.length > 0) {
        updateComplexElementDetailsBulk(updates);
      }
      changesRef.current = false;
    }
    onEnd();
  };

  const handleCheckboxResize = (target, lastEvent, accumulatedUpdates = undefined, activeUuids = []) => {
    const selectedTargetType = target.getAttribute('data-element-type');
    if (
      selectedTargetType === FILLABLE_CHECKBOX_TYPE ||
      selectedTargetType === FILLABLE_RADIO_BUTTON_TYPE
    ) {
      const currentUuid = target.getAttribute('data-uuid');
      const el = storedElements.find(el => el.uuid === currentUuid);
      if (el) {
        const filteredElements = storedElements.filter(
          els =>
            el.uuid !== els.uuid &&
            selectedTargetType === els?.fillableType &&
            els?.formOptions?.groupName === el?.formOptions?.groupName,
        );
        filteredElements.forEach(e => {
          if (activeUuids.includes(e.uuid)) return;
          const subTarget = document.querySelector(`[data-uuid='${e.uuid}']`);
          handleStoredElementUpdates(subTarget, lastEvent?.direction, undefined, accumulatedUpdates);
        });
      }
    }
  };

  const getMoveableClassname = targets => {
    if (targets && targets.length === 1) {
      const isDragging = moveableRef?.current?.isDragging();
      if (
        targets[0] &&
        targets[0].getAttribute('data-element-type') &&
        (targets[0].getAttribute('data-element-type') === 'line' ||
          targets[0].getAttribute('data-element-type') === 'doubleLine')
      ) {
        return `line-element-controls ${isDragging ? 'is-dragging' : ''}`;
      } else if (targets[0].clientHeight && targets[0].clientHeight < 40) {
        return `controls-with-opacity ${isDragging ? 'is-dragging' : ''}`;
      } else if (isDragging) {
        return 'is-dragging';
      }
    }
    return true;
  };

  const isReferenced = targets[0] &&
    targets[0].getAttribute('data-element-type') &&
    targets[0].getAttribute('data-element-type') === 'referenced';

  const isBlockElement = targets[0] && // TODO : Remove block elements from targets -> its not a moveable
  targets[0]?.parentElement?.parentElement?.getAttribute('data-element-type') === 'referenced';

  const isStaticTableElement = targets[0] && (targets[0].getAttribute('data-element-type') === STATIC_TABLE_ELEMENT);
  const isTableElement = targets[0] && (targets[0].getAttribute('data-element-type') === 'dynamic_table');
  const isOverflowTableElement = targets[0] && (targets[0].getAttribute('data-element-type') === 'overflow-table');
  const isGroupElement = targets[0] && Array.from(targets[0].classList).includes('group-selectable');
  const isGroupSubEl = targets[0] && Array.from(targets[0].classList).includes('group-selectable-el');
  const isLineElement = targets[0] && ['doubleLine', 'line'].includes(targets[0].getAttribute('data-element-type'));
  const isDynamicGroupSubEl = isGroupSubEl && targets[0]?.parentElement?.parentElement.getAttribute('data-is-variable') === '1';

  let selectedElement;
  if (targets[0]?.getAttribute('data-uuid')) {
    selectedElement = storedElements.find(elem =>
      targets[0]?.getAttribute('data-uuid') === elem.uuid
    );
  }
  let selectedElementId, posX = 0, posY = 0;
  selectedElementId = isReferenced ? targets[0]?.childNodes[0]?.getAttribute('data-reference-id') : targets[0];
  if (selectedElement) {
    posX = getFrame(targets[0], selectedElement.style).get('tx').replace("px", "");
    posY = getFrame(targets[0]).get('ty').replace("px", "");
  }

  let position = { top: 0, left: 0 };
  const pageTarget = document.getElementById(`editingContainer-${pageId}`);
  let targetEleWidth = targets && targets[0] && targets[0]?.style?.width || 0;
  targetEleWidth = targetEleWidth !== 0 && targetEleWidth.replace(/px/,'')*1;
  if (pageTarget) {
    const { left: pageLeft, top: pageTop } = pageTarget.getBoundingClientRect();
    const containerTarget = document.getElementById(pageId);
    const { left: containerLeft, top: containerTop } = containerTarget.getBoundingClientRect();
    position = {
        top: containerTop - pageTop + parseInt(posY) - 30,
        left: containerLeft - pageLeft + parseInt(posX),
        rightPlacement: containerLeft - pageLeft + parseInt(posX) + targetEleWidth,
      };
  }

  let handlers = ['nw', 'ne', 'sw', 'se'];
  if (!((targets && targets.length > 1) || isReferenced)) {
    if (targets[0] &&
      targets[0].getAttribute('data-element-type') &&
      (targets[0].getAttribute('data-element-type') === 'line' ||
        targets[0].getAttribute('data-element-type') === 'doubleLine')) {
      handlers = ['e', 'w'];
    } else if (isBlockElement) {
      handlers = [];
    } else {
      if (isTableElement) {
        handlers = handlers.concat(['n', 's']);
      } else if (targets[0] && (targets[0].getAttribute('data-element-type') === 'rectangle')) {
        handlers = handlers.concat(['n', 's', 'e', 'w']);
      } else if (
        targets[0] &&
        (targets[0].getAttribute('data-element-type') === FILLABLE_TEXT_INPUT_TYPE ||
        targets[0].getAttribute('data-element-type') === FILLABLE_COMBO_BOX_TYPE ||
        targets[0].getAttribute('data-element-type') === FILLABLE_CHECKBOX_TYPE ||
        targets[0].getAttribute('data-element-type') === 'checkboxGroup' ||
        targets[0].getAttribute('data-element-type') === FILLABLE_RADIO_BUTTON_TYPE ||
        targets[0].getAttribute('data-element-type') === 'radiobuttongroup')) {
        if(targets[0].getAttribute('data-inputbox-enablescroll') === 'true' || targets[0].getAttribute('data-is-variable') === '1') {
          handlers = handlers.concat(['n', 's', 'e', 'w']);
        } else if (targets[0].getAttribute('data-element-type') !== FILLABLE_CHECKBOX_TYPE &&
          targets[0].getAttribute('data-element-type') !== FILLABLE_RADIO_BUTTON_TYPE &&
          targets[0].getAttribute('data-element-type') !== 'radiobuttongroup' &&
          targets[0].getAttribute('data-element-type') !== 'checkboxGroup') {
          handlers = handlers.concat(['e', 'w']);
        } else if (targets[0].getAttribute('data-element-type') === FILLABLE_CHECKBOX_TYPE || targets[0].getAttribute('data-element-type') === FILLABLE_RADIO_BUTTON_TYPE) {
          handlers = ['se']
        }
      } else if (targets[0] && targets[0].getAttribute('data-element-type') === FILLABLE_SIGNATURE_TYPE) {
        handlers = handlers.concat(['n', 's', 'e', 'w']);
      } else if (isStaticTableElement) {
        handlers = ['se','e'];
      } else {
        if(targets[0] && (targets[0].getAttribute('data-element-type') !== 'circle'))
        handlers = handlers.concat(['e', 'w']);
      }
    }
  }

  const isDraggable = !isBlockElement && !isDynamicGroupSubEl && !(isTableElement && isTableEditing);
  const isResizable = !isBlockElement && !isDynamicGroupSubEl && !(isTableElement && isTableEditing) && !isOverflowTableElement;
  const isRotatable = !isBlockElement && !isTableElement && !isStaticTableElement && !isOverflowTableElement && !isGroupSubEl && !(targets[0] && (targets[0].getAttribute('data-element-type') === 'circle')); // Table is not rotatable
  const extraMoveableProps = {};
  if (isGroupElement && selectedElement.elementDetails.editing) {
    extraMoveableProps.dragTarget = targets[0].parentElement;
  }

  useEffect(() => {
    setShowAccessibility(false);
    setAccessibilityPopupVisible(false);
    setShowImageLink(false)
    setImageLinkPopupVisible(false);
  }, [targets && targets[0]]);

  useEffect(() => {
    getTargets.current = targets;
    if(moveableRef.current && (targets.length!==1 || targets[0].getAttribute('data-element-type') === TEXT_ELEMENT)) {
      const isAccessibilityIcon = moveableRef.current.moveable.controlBox.element.lastChild.className.includes('accessibility-icon');
      if(isAccessibilityIcon){
        setShowAccessibility(false);
        setShowImageLink(false);
        // moveableRef.current.moveable.controlBox.element.removeChild(moveableRef.current.moveable.controlBox.element.lastChild);
      }
      
    }
    if (moveableRef.current && targets.length===1 && targets[0].getAttribute('data-element-type') !== TEXT_ELEMENT) {
      const controlBox = moveableRef.current.moveable.controlBox;
      if (controlBox && !isEmailTemplate) {
        setShowAccessibility(true);
        requestAnimationFrame(() =>{
          positionAccessibilityIcon();
        })
      }
      if (controlBox && isEmailTemplate && targets[0] && targets[0].getAttribute('data-element-type') === IMAGE_ELEMENT) {
        setShowImageLink(true)
        requestAnimationFrame(() =>{
          positionAccessibilityIcon();
        })
      }
    } 
  }, [targets]);

  const onClickGroupHandler = e => {
    const { groupId } = getGroupIdAndNonGroupElements(e.targets);
    if (groupId) {
      if (e.containsTarget) {
        const target = e.targets[e.targetIndex];
        const event = {
          inputEvent: e.inputEvent,
          containsTarget: e.containsTarget,
          inputTarget: e.inputTarget,
          target: e.targets[e.targetIndex],
          isDouble: e.isDouble,
          clientX: e.clientX,
          clientY: e.clientY,
        };
        Array.from(document.getElementsByClassName('group-element-selected')).forEach(element => {
          element.classList.remove('group-element-selected');
        });
        if (moveableRef.current) {
          const styleObject = {
            height: moveableRef.current.moveable.areaElement.style.height,
            width: moveableRef.current.moveable.areaElement.style.width,
          };
          event.reAddAreaElementStyles = () => {
            if (moveableRef.current?.moveable?.areaElement) {
              moveableRef.current.moveable.areaElement.style.height = styleObject.height;
              moveableRef.current.moveable.areaElement.style.width = styleObject.width;
            }
          };
          event.removeAreaElementStyles = () => {
            if (moveableRef.current?.moveable?.areaElement) {
              moveableRef.current.moveable.areaElement.style.height = 0;
              moveableRef.current.moveable.areaElement.style.width = 0;
            }
          };
          moveableRef.current.moveable.moveables[e.targetIndex].controlBox.element.classList.add(
            'group-element-selected',
          );
          if (e.isDouble) {
            moveableRef.current.moveable.triggerEvent('onClick', event, true);
          }
        }
        const targetUuid = target?.getAttribute('data-uuid');
        if (targetUuid) {
          setSelectedGroupElementId(targetUuid);
        }
      }
    } else {
      selectoRef.current.clickTarget(e.inputEvent, e.inputTarget);
    }
  };

  const updateGroupSelection = els => {
    if (els?.length > 0) {
      els.forEach(data => {
        let styles = data.style.cssText;
        styles = stylesToJson(styles);
        getFrame(data, styles);
      });
      setAllTargets(els);
      props.handleSelectedElements(els);
      setViewPort(els);
      moveableRef.current && moveableRef.current.updateRect();
    }
  };

  const onSelectEndGroupHandler = e => {
    const { groupId, nonGroupElements, groupElements } = getGroupIdAndNonGroupElements(e.selected);
    if (groupId) {
      const els = Array.from(
        new Set([
          ...Array.from(document.querySelectorAll(`[data-group-uuid='${groupId}']`)),
          ...groupElements,
          ...nonGroupElements,
        ]),
      );
      updateGroupSelection(els);
      return true;
    } else {
      Array.from(document.getElementsByClassName('group-element-selected')).forEach(element => {
        element.classList.remove('group-element-selected');
      });
      setSelectedGroupElementId();
      return false;
    }
  };

  const moveablePortalElementContainer = `editingContainer-${props.scrollPageId}`;
  const dataElementType = targets[0] && targets[0].getAttribute('data-element-type')
  return (
    <>
    {!isInteractiveDesign &&
        targets?.length === 1 &&
        moveableRef?.current?.moveable &&
        (showAccessibility || showImageLink) &&
        ReactDOM.createPortal(
          <AccessibilityIcon
            accessibilityVisible={accessibilityPopupVisible}
            setAccessibilityPopupVisible={setAccessibilityPopupVisible}
            linkVisible={imageLinkPopupVisible}
            setImageLinkPopupVisible={setImageLinkPopupVisible}
            elementUUID={targets[0]?.getAttribute('data-uuid')}
            dataElementType={dataElementType}
            showAccessibility={showAccessibility}
            showImageLink={showImageLink}
          />,
          moveableRef.current.moveable.controlBox.element,
        )}
    {props.editable && ReactDOM.createPortal(
      <>
      
      <Moveable
        ref={moveableRef}
        ables={[CustomAble]}
        props={{
            customAble: false
        }}
        target={!staticTableBlock ? targets : null}
        className={getMoveableClassname(targets)}
        edgeDraggable={true}
        padding={{ left: 0, top: 0, right: 0, bottom: 0 }}
        container={null}
        renderDirections={handlers}
        origin={false}
        checkInput={!isReferenced && isElementEditing && !isGroupElement && !isGroupSubEl}
        keepRatio={!isLineElement}
        draggable={isDraggable}
        resizable={isResizable}
        rotatable={isRotatable}
        elementGuidelines={elementGuidelines}
        snappable={canSnap}
        rotationPosition={position?.top < 40 ? "bottom" : "top"}
        verticalGuidelines={[0, 200, 400]}
        horizontalGuidelines={[0, 200, 400]}
        snapThreshold={snapThreshold}
        isDisplaySnapDigit={true}
        snapGap={true}
        snapElement={true}
        snapVertical={true}
        snapHorizontal={true}
        snapCenter={true}
        snapDigit={0}
        snapDistFormat={distance => {
          return `${(distance * (25.4 / 96)).toFixed(1)}mm`;
        }}
        throttleDrag={0}
        throttleResize={0}
        throttleRotate={0}
        rotationAtCorner={false}
        onClick={e => {
          // console.log("On click moveable called!! ", e, e.isDouble);
          const inputTargetParent = e.inputTarget.parentElement;
          // don't allows mouse click from quill link popup. This is to fix quill link functionality not working in certain scenarios.
          if (e && e.containsTarget && !e.inputTarget.classList.contains('ql-tooltip') && !inputTargetParent.classList.contains('ql-tooltip')) {
            const isEditing =
              e.target.getAttribute('data-element-type') === STATIC_TABLE_ELEMENT
                ? e.inputEvent.detail === 2 || e.isDouble
                : e.inputTarget.offsetParent?.classList?.contains('ql-preview') ||
                  e.inputTarget.__blot !== undefined ||
                  e.inputTarget.__quill !== undefined;
            setElementEditing(isEditing);
            if (e.target.getAttribute('data-element-type') === TEXT_ELEMENT || e.target.getAttribute('data-element-type') === FILLABLE_TEXT_INPUT_TYPE) {
              const quillContainer = e.target.querySelector(`#q-container-${e.target.getAttribute('data-uuid')}`);
              console.log("Click is on a text element ", quillContainer);
              // onMovableClick(e.target.getAttribute('data-uuid'));
              if (quillContainer) {
                const detail = {
                  clientX: e.clientX,
                  clientY: e.clientY,
                  reAddAreaElementStyles: e.reAddAreaElementStyles,
                  removeAreaElementStyles: e.removeAreaElementStyles,
                };
                quillContainer.dispatchEvent(new CustomEvent('elementFocus', { detail }));
              }
            }
          }
        }}
        onClickGroup={onClickGroupHandler}
        onRender={handleRender}
        onRenderGroup={handleRenderGroup}
        onDragStart={e => {
          setStartingCoords({ x: e.clientX, y: e.clientY });
          return handleDragStart(e);
        }}
        // onDragStart={handleDragStart}
        onDrag={handleDrag}
        onDragEnd={(props) => {
          setStartingCoords(null);
          handleElementUpdates(props,'ON_DRAG_END');
        }}
        onRotateStart={handleRotateStart}
        onRotate={handleRotate}
        onRotateEnd={(props)=>handleElementUpdates(props,'ON_ROTATE_END')}
        onResizeStart={handleResizeStart}
        onResize={handleResize}
        onResizeEnd={(props)=>handleElementUpdates(props,'ON_RESIZE_END')}
        onScale={handleScale}
        // Groups
        onDragGroupStart={handleDragGroupStart}
        onDragGroup={handleDragGroup}
        onDragGroupEnd={onGroupEnd}
        onRotateGroupStart={handleRotateGroupStart}
        onRotateGroup={handleRotateGroup}
        onRotateGroupEnd={onGroupEnd}
        onResizeGroupStart={handleResizeGroupStart}
        onResizeGroup={handleResizeGroup}
        onResizeGroupEnd={onGroupEnd}
        onScaleGroup={handleScaleGroup}
      
        {...extraMoveableProps}
      /></>, document.getElementById(moveablePortalElementContainer))}
      {isReferenced && targets.length === 1 && refElNameVisible && (
        <RefTag
        style={{
          top:position.top,
          left:position.left
        }}>
          <p>{t(DOCUMENT_DESIGN_SECTION.REFERENCED_ELEMENT_LABEL.key, DOCUMENT_DESIGN_SECTION.REFERENCED_ELEMENT_LABEL.value)}</p>
        {selectedElementId &&
          <Tooltip title={t(DOCUMENT_DESIGN_SECTION.EDIT_REFERENCED_ELEMENT_TOOLTIP.key, DOCUMENT_DESIGN_SECTION.EDIT_REFERENCED_ELEMENT_TOOLTIP.value)}>
            <Icon type="edit" onClick={() => window.open(`/elementDesign/${selectedElementId}`)} />{' '}
          </Tooltip>
        }
        </RefTag>
      )}
      {props.editable && ReactDOM.createPortal(
      <Selecto
        ref={selectoRef}
        dragContainer={`[id='${props.pageId}']`}
        selectableTargets={props.editable ? ['.selectable'] : ['.no-selectoin']} // Added the no-selection to disable the element selection if the template is locked
        hitRate={0}
        selectByClick={true}
        selectFromInside={props.editable && !isReferenced && isElementEditing}
        toggleContinueSelect={['shift']}
        checkInput={!isReferenced && isElementEditing && !isGroupElement && !isGroupSubEl}
        onDragStart={e => {
          const moveable = moveableRef.current;
          const target = e.inputEvent.target;
          if (
            moveable.isMoveableElement(target) ||
            (targets && Array.from(targets).some(t => t === target || t.contains(target)))
          ) {
            e.stop();
          }
        }}
        onSelect={e => {
          let selected = e.selected;
          const selectedGroupElements = e.selected.filter(t => Array.from(t.classList).includes('group-selectable-el'));
          if (selectedGroupElements.length < e.selected.length) {
            selected = selected.filter(t => !Array.from(t.classList).includes('group-selectable-el'));
          } else if (selectedGroupElements.length === 1) {
            const parentElement = selectedGroupElements[0].parentElement.parentElement;
            if (parentElement.getAttribute('data-is-variable') === '1') {
              const type = selectedGroupElements[0].getAttribute('data-element-type');
              selected = Array.from(parentElement.querySelectorAll('.group-selectable-el')).filter(subTarget => subTarget.getAttribute('data-element-type') === type);
            }
          }
          if (e.inputEvent?.target?.id !== 'addToToc') {
            setAllTargets(selected);
          }
          selected.forEach(data => {
            let styles = data.style.cssText;
            styles = stylesToJson(styles);
            getFrame(data, styles);
          });
        }}
        onSelectEnd={e => {
          const isGroupHandled = onSelectEndGroupHandler(e);
          if (!isGroupHandled) {
            e.selected.forEach(data => {
              let styles = data.style.cssText;
              styles = stylesToJson(styles);
              getFrame(data, styles);
            });
            
            if (e.inputEvent?.target?.id !== 'addToToc') {
              setAllTargets(e.selected);
  
              props.handleSelectedElements(e.selected);
  
              // set target view port width
              if (e.selected && e.selected.length > 0) {
                setViewPort(e.selected);
              }
            }
          }
          if (e.isDragStart) {
            e.inputEvent.preventDefault();
          }
        }}
      />, document.getElementById(`scroll-${props.scrollPageId}`))}
    <div className="moveable-label" />
    {targets &&
        targets.length === 1 &&
        !isElementEditing &&
        document.getElementById(ELEMENT_POSITION_TOOLBAR_ID) &&
        ReactDOM.createPortal(
          <ElementPositionToolbar
            getFrame={getFrame}
            targetPosition={targetPosition}
            targets={targets}
            updateStoredElementDetails={updateStoredElementDetails}
            selectedElement={storedElements.find((el) => el.uuid === targets[0].getAttribute('data-uuid'))}
            measurement={isEmailTemplate ? 'px' : 'mm'}
          />,
          document.getElementById(ELEMENT_POSITION_TOOLBAR_ID),
        )}
    </>
  );
};
export default Selectable;